import modules from '@/sui/data/modules'
import doughnut from '@/sui/tools/chart/doughnut'
import polar from '@/sui/tools/chart/polar'
import bar from '@/sui/tools/chart/bar'
import merge from '@/sui/tools/merge'

const protocols = {}
const protocolLabels = {}
const colors = {}

const years = [
  '2019',
  '2020',
  '2021',
  '2022',
  '2023',
  '2024',
  '2025',
  '2026'
]
years.forEach(
  y => {
    const ymod = modules[y]
    protocols[y] = []
    protocolLabels[y] = []
    colors[y] = []
    for (let m in ymod) {
      const mod = ymod[m]
      if (mod.sheets != null) {
        protocols[y].push(m)
        protocolLabels[y].push(mod.label)
        colors[y].push(mod.color)
      }
    }
  }
)

const bactLabels = [
  'Validée sans bactériémie',
  'Aucune fiche saisie',
  '1 fiche et plus'
]

const buckets = [
  1,
  11,
  21,
  51,
  101,
  201,
  501
]

function establishmentCount (year, data) {
  const values = []
  for (let i in data) {
    values.push(data[i].count)
  }

  return doughnut.classic(
    'establishment-count',
    'Nombre de modalités avec des fiches saisies',
    [
      {
        fill: true,
        backgroundColor: colors[year],
        data: values
      }
    ],
    protocolLabels[year]
  )
}

function protocolDistribution (year, data) {
  return doughnut.classic(
    'protocol-distribution',
    'Nombre de fiches saisies par protocole',
    [
      {
        fill: true,
        backgroundColor: colors[year],
        data: data
      }
    ],
    protocolLabels[year]
  )
}

function bactSheetsDistribution (data) {
  return doughnut.classic(
    'bact-sheets-distribution',
    'Répartition des établissements par nombre de fiches saisies dans Bact ADI',
    [
      {
        fill: true,
        backgroundColor: [
          '#71ce3c',
          '#cccccc',
          '#25b4f2'
        ],
        data: data
      }
    ],
    bactLabels
  )
}

function sheetByEstablishment (year, data) {
  const values = []
  protocols[year].forEach(
    p => {
      values.push(data[p].avg)
    }
  )

  return polar.classic(
    'sheet-by-establishment',
    'Nombre de fiches moyen par modalité',
    [
      {
        fill: true,
        backgroundColor: colors[year],
        data: values
      }
    ],
    protocolLabels[year]
  )
}

function hbar (labels, data, title, color, borderColor, valueColor) {
  const result = bar.classic(
    `hbar-${title}`,
    title,
    [
      {
        backgroundColor: color,
        borderColor: color,
        borderWidth: 1,
        data: data
      }
    ],
    labels
  )

  result.type = 'hbar'
  result.config.options.tooltips = { enabled: false }
  result.config.options.rover = { mode: null }
  result.config.options.legend = { display: null }
  result.config.options.plugins.datalabels = merge(
    result.config.options.plugins.datalabels, {
      anchor: 'middle',
      align: 'center',
      color: valueColor
    }
  )

  result.config.options.yAxis = {
    beginAtZero: true
  }
  result.config.options.xAxis = {
    beginAtZero: true
  }
  return result
}

function regionalEstablishmentDistribution (labels, data) {
  return hbar(
    labels,
    data,
    `Nombre d'établissements avec fiches par région`,
    '#7ef1ffff',
    '#18affc8a',
    '#916c4e'
  )
}

function regionalDistribution (labels, data) {
  return hbar(
    labels,
    data,
    'Nombre de fiches saisies par région',
    '#fff17eff',
    '#fcaf1e8a',
    '#916c4e'
  )
}

function protocolRadar (year, data) {
  const color = '#a9a9a9'
  const datasets = [
    {
      label: 'valeur',
      fill: true,
      backgroundColor: `${color}80`,
      borderColor: `${color}`,
      pointBackgroundColor: `${color}`,
      data: []
    }
  ]

  for (let i = 0; i !== protocols[year].length; ++i) {
    const d = data[protocols[year][i]]
    datasets[0].data.push(d.rate)
  }
  return {
    options: {
      tooltips: { enabled: false },
      scale: {
        ticks: {
          min: 0,
          max: 100,
          stepSize: 20
        }
      },
      title: {
        display: true,
        text: `%age d'établissements ayant commencé la saisie`
      },

      plugins: {
        datalabels: {
          color: '#ffffff',
          font: {
            weight: 'bold'
          },
          backgroundColor: function (context) {
            return colors[year][context.dataIndex]
          },
          borderRadius: 4,

          formatter: function (value, context) {
            return `${Math.round(value)}`
          }
        }
      },
      legend: {
        display: false
      }
    },
    data: {
      labels: protocolLabels[year],
      datasets: datasets
    }
  }
}

function establishmentSizeDistribution (data) {
  return bar.classic(
    'establishment-size-distribution',
    `Répartition des établissements par nombre de fiches saisies`,
    [
      {
        fill: true,
        backgroundColor: [
          '#EFC74Ddd',
          '#E0A74Fdd',
          '#F5944Cdd',
          '#DE5E43dd',
          '#FA252Add',
          '#8FC74Ddd',
          '#80A74Fdd',
          '#9A252Add'
        ],
        data: data
      }
    ],
    [
      'Aucune fiche',
      '1 à 10 fiches',
      '11 à 20 fiches',
      '21 à 50 fiches',
      '51 à 100 fiches',
      '101 de 200 fiches',
      '201 à 500 fiches',
      'Plus de 500 fiches'
    ]
  )
}

function evolution (year, data) {
  const values = []
  protocols[year].forEach(() => values.push(0))
  values.push(0)

  const datasets = []
  for (let i = 0; i !== protocols[year].length; ++i) {
    datasets.push(
      {
        fill: false,
        label: protocolLabels[year][i],
        backgroundColor: colors[year][i],
        borderColor: colors[year][i],
        data: []
      }
    )
  }

  datasets.push(
    {
      fill: false,
      label: 'total',
      backgroundColor: '#de5c4aff',
      borderColor: '#de5c4aff',
      data: []
    }
  )

  const total = datasets.length - 1
  const count = data.length
  const dataTotal = datasets[total].data
  for (let j = 0; j !== count; ++j) {
    const d = data[j]
    const forceDot = j === 0 || j === count - 1
    for (let i = 0; i !== protocols[year].length; ++i) {
      const p = protocols[year][i]
      const v = d.data[p]
      if (forceDot || v > 0) {
        values[i] += d.data[p]
        datasets[i].data.push({
          t: d.date,
          y: values[i]
        })
      }
    }
    const t = d.data.total
    if (forceDot || t > 0) {
      values[total] += t
      if (dataTotal.length === 0 ||
          dataTotal[dataTotal.length - 1].t !== d.date) {
        dataTotal.push({
          t: d.date,
          y: values[total]
        })
      } else {
        dataTotal[dataTotal.length - 1].y = values[total]
      }
    }
  }

  return {
    options: {
      layout: {
        padding: {
          left: 20,
          right: 20
        }
      },
      legend: {
        position: 'bottom'
      },
      scales: {
        xAxes: [{
          type: 'time',
          time: {
            tooltipFormat: 'll'
          },
          scaleLabel: {
            display: true,
            labelString: 'Date'
          }
        }]
      },
      title: {
        display: true,
        text: `Évolution du nombre de fiches saisies`
      },

      plugins: {
        datalabels: {
          backgroundColor: function (context) {
            return context.dataset.backgroundColor
          },
          color: '#ffffff',
          borderRadius: 4,
          align: 'top',
          font: { weight: 'bold' },
          display: function (context) {
            const index = context.dataIndex
            const count = context.dataset.data.length
            return index === count - 1
          },
          formatter: function (value, context) {
            return value.y
          }
        }
      }
    },

    data: {
      datasets: datasets
    }
  }
}

export default {
  protocols: protocols,
  bactLabels: bactLabels,
  buckets: buckets,
  status: status,
  protocolDistribution: protocolDistribution,
  regionalDistribution: regionalDistribution,
  regionalEstablishmentDistribution: regionalEstablishmentDistribution,
  protocolRadar: protocolRadar,
  establishmentCount: establishmentCount,
  establishmentSizeDistribution: establishmentSizeDistribution,
  evolution: evolution,
  bactSheetsDistribution: bactSheetsDistribution,
  sheetByEstablishment: sheetByEstablishment
}
